/* Mobile tier selector + sticky buy bar (pricing-cards section).

   Why this exists: at <=900px `.pc-pricing__grid` collapses to one column, so
   the three packages stack. Each card is ~569px tall in an 812px viewport,
   which means a buyer can never see two prices at once and choosing between
   tiers becomes a memory test across ~2.15 screens. Desktop shows all three
   in a single 0.65-screen row. Measured bounce on the commercial templates is
   78.4% on mobile against 47.3% on desktop; the homepage, which has no tier
   grid, shows no such gap.

   Two behaviours, both mobile-only and both progressive enhancement — with no
   JS the cards stay stacked exactly as they are today:

   1. Segmented selector. A tablist of tier name + price sits above the grid
      and one card body shows at a time, so all prices are comparable in a
      single screen and switching costs no scrolling.

   2. Sticky buy bar. Once the grid scrolls out of view there is no price and
      no CTA anywhere on screen for the remaining ~10 screens of page. The bar
      carries the selected tier's name, price and primary action.

   Decks (data-ppc-deck) are toggled by pc-pricing.js via the `hidden`
   attribute as the state/stage/role questions are answered, so we observe
   those mutations rather than caching a single "active" deck at init. */
(function () {
  'use strict';

  var MQ = '(max-width: 900px)';
  var mql = window.matchMedia(MQ);
  var instances = [];
  var bar = null;
  var barObserver = null;
  var barGrid = null;
  var barEnabled = true;
  var selectEnabled = true;

  function text(el) { return el ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }

  /* The primary action on a card. Deliberately excludes the secondary CTA
     (.pc-tier__cta2), the alternate-edition link and "View full course
     details" — the bar should repeat the buying action, not a way out of it. */
  function primaryCta(card) {
    var candidates = card.querySelectorAll('.pc-btn');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.closest('.pc-tier__cta2')) continue;
      return el;
    }
    return null;
  }

  function priceEl(card) { return card.querySelector('.pc-tier__amount'); }

  // ---------------------------------------------------------------- selector

  function Segmented(deck) {
    this.deck = deck;
    this.grid = deck.querySelector('.pc-pricing__grid');
    this.cards = this.grid
      ? Array.prototype.slice.call(this.grid.querySelectorAll('.pc-tier'))
      : [];
    this.tablist = null;
    this.tabs = [];
    this.index = 0;
    this.built = false;

    /* Open on the anchor tier when there is one. It is the tier the section is
       already merchandised around, and opening on "most popular" is a weaker
       default than opening on nothing only when no anchor is set. */
    var anchor = this.cards.indexOf(this.grid && this.grid.querySelector('.pc-tier--anchor'));
    this.index = anchor > -1 ? anchor : 0;
  }

  Segmented.prototype.build = function () {
    if (this.built || this.cards.length < 2) return;
    var self = this;

    var list = document.createElement('div');
    list.className = 'pc-tierseg';
    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-label', 'Choose a package');

    this.cards.forEach(function (card, i) {
      var id = (card.id = card.id || 'pc-tier-' + Math.random().toString(36).slice(2, 9));
      var tabId = id + '-tab';

      var tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'pc-tierseg__tab';
      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);

      var name = document.createElement('span');
      name.className = 'pc-tierseg__name';
      name.textContent = text(card.querySelector('.pc-tier__name'));

      var price = document.createElement('span');
      price.className = 'pc-tierseg__price';
      price.textContent = text(priceEl(card));

      tab.appendChild(name);
      tab.appendChild(price);

      if (card.classList.contains('pc-tier--anchor')) tab.classList.add('is-anchor');

      tab.addEventListener('click', function () { self.select(i, true); });
      tab.addEventListener('keydown', function (e) { self.onKey(e, i); });

      list.appendChild(tab);
      self.tabs.push(tab);

      /* Cards are listitems in the stacked layout; as panels they must not
         keep that role or the tablist announces a list inside itself. */
      if (!card.dataset.pcRole) card.dataset.pcRole = card.getAttribute('role') || '';
      card.setAttribute('role', 'tabpanel');
      card.setAttribute('aria-labelledby', tabId);
    });

    if (!this.grid.dataset.pcRole) this.grid.dataset.pcRole = this.grid.getAttribute('role') || '';
    this.grid.removeAttribute('role');
    this.grid.classList.add('pc-pricing__grid--seg');
    this.grid.parentNode.insertBefore(list, this.grid);

    this.tablist = list;
    this.built = true;
    this.select(this.index, false);
  };

  Segmented.prototype.select = function (i, userInitiated) {
    if (i < 0 || i >= this.cards.length) return;
    this.index = i;

    this.cards.forEach(function (card, n) {
      var on = n === i;
      card.hidden = !on;
      card.classList.toggle('is-active', on);
    });

    this.tabs.forEach(function (tab, n) {
      var on = n === i;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      tab.classList.toggle('is-active', on);
    });

    if (userInitiated) this.tabs[i].focus();
    syncBar();
  };

  Segmented.prototype.onKey = function (e, i) {
    var last = this.cards.length - 1;
    var next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    this.select(next, true);
  };

  Segmented.prototype.destroy = function () {
    if (!this.built) return;
    if (this.tablist && this.tablist.parentNode) this.tablist.parentNode.removeChild(this.tablist);
    this.cards.forEach(function (card) {
      card.hidden = false;
      card.classList.remove('is-active');
      card.removeAttribute('aria-labelledby');
      if (card.dataset.pcRole) card.setAttribute('role', card.dataset.pcRole);
      else card.removeAttribute('role');
    });
    if (this.grid) {
      this.grid.classList.remove('pc-pricing__grid--seg');
      if (this.grid.dataset.pcRole) this.grid.setAttribute('role', this.grid.dataset.pcRole);
    }
    this.tablist = null;
    this.tabs = [];
    this.built = false;
  };

  Segmented.prototype.activeCard = function () {
    return this.built ? this.cards[this.index] : null;
  };

  // ----------------------------------------------------------------- buy bar

  function buildBar() {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'pc-buybar';
    bar.hidden = true;
    bar.innerHTML =
      '<div class="pc-buybar__inner">' +
        '<div class="pc-buybar__meta">' +
          '<span class="pc-buybar__name"></span>' +
          '<span class="pc-buybar__price"></span>' +
        '</div>' +
        '<div class="pc-buybar__action"></div>' +
      '</div>';
    document.body.appendChild(bar);
    return bar;
  }

  /* The deck pc-pricing.js is currently showing. Decks not in play carry the
     `hidden` attribute, and a section can hold many of them. */
  function visibleInstance() {
    for (var i = 0; i < instances.length; i++) {
      var inst = instances[i];
      if (!inst.built) continue;
      if (inst.deck.hasAttribute('hidden')) continue;
      if (!inst.deck.offsetParent && inst.deck.offsetHeight === 0) continue;
      return inst;
    }
    return null;
  }

  function showBar(on) {
    if (!bar) return;
    if (bar.hidden === !on) return;
    bar.hidden = !on;
    document.body.classList.toggle('pc-buybar-on', on);
  }

  function syncBar() {
    if (!barEnabled || !mql.matches) { showBar(false); return; }
    var inst = visibleInstance();
    var card = inst && inst.activeCard();
    if (!card) { showBar(false); watchGrid(null); return; }

    buildBar();
    bar.querySelector('.pc-buybar__name').textContent = text(card.querySelector('.pc-tier__name'));
    bar.querySelector('.pc-buybar__price').textContent = text(priceEl(card));

    var slot = bar.querySelector('.pc-buybar__action');
    var cta = primaryCta(card);
    slot.innerHTML = '';
    if (cta) {
      /* Clone rather than rebuild: the clone keeps href, target/rel and the
         inline arrow icon, and a disabled "opening soon" tier stays disabled
         in the bar instead of turning into a dead link. */
      var clone = cta.cloneNode(true);
      clone.classList.remove('pc-btn--full', 'pc-btn--lg');
      clone.classList.add('pc-btn--sm');
      clone.removeAttribute('id');
      slot.appendChild(clone);
    }

    watchGrid(inst.grid);
  }

  /* Show the bar only once the grid has been scrolled PAST — while the cards
     are on screen the bar would just repeat what the buyer is already reading,
     and it would cover the bottom card's own button. */
  function watchGrid(grid) {
    if (grid === barGrid) return;
    if (barObserver) { barObserver.disconnect(); barObserver = null; }
    barGrid = grid;
    if (!grid || !('IntersectionObserver' in window)) return;

    barObserver = new IntersectionObserver(function (entries) {
      var e = entries[0];
      var scrolledPast = !e.isIntersecting && e.boundingClientRect.bottom < 0;
      showBar(scrolledPast);
    }, { threshold: 0 });
    barObserver.observe(grid);
  }

  // -------------------------------------------------------------- lifecycle

  function collect() {
    instances.forEach(function (i) { i.destroy(); });
    instances = [];
    // Re-read the section settings each time: a theme-editor re-render can flip them.
    selectEnabled = true;
    barEnabled = true;
    var roots = document.querySelectorAll('[data-pc-pricing]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.pcTierSelect === 'off') selectEnabled = false;
      if (root.dataset.pcBuyBar === 'off') barEnabled = false;
      Array.prototype.forEach.call(root.querySelectorAll('[data-ppc-deck]'), function (deck) {
        instances.push(new Segmented(deck));
      });
    });
  }

  function apply() {
    if (mql.matches && selectEnabled) instances.forEach(function (i) { i.build(); });
    else instances.forEach(function (i) { i.destroy(); });
    syncBar();
  }

  function watchDecks() {
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function () { syncBar(); });
    instances.forEach(function (i) {
      mo.observe(i.deck, { attributes: true, attributeFilter: ['hidden'] });
    });
  }

  function init() {
    collect();
    if (!instances.length) return;
    apply();
    watchDecks();

    if (mql.addEventListener) mql.addEventListener('change', apply);
    else if (mql.addListener) mql.addListener(apply);

    document.addEventListener('pc:tabs-sync', syncBar);
    window.addEventListener('pageshow', syncBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-pc-pricing]')) {
      showBar(false);
      init();
    }
  });
})();
